import {Injectable, Optional} from '@angular/core';
import {Address} from '@kion/data-models';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';
import {Observable, throwError} from "rxjs";
import {catchError, map, switchMap} from 'rxjs/operators';

interface IResponseItem {
  id: string,
  cellphone?: string,
  companyName: string,
  country: {
    isocode?: string,
    name: string
  },
  defaultAddress: boolean,
  district?: string,
  email: string,
  firstName: string,
  formattedAddress: string,
  lastName: string,
  line1: string,
  line2: string,
  phone?: string,
  postalCode: string,
  shippingAddress: boolean,
  title: string,
  titleCode?: string,
  town: string,
  visibleInAddressBook: boolean,
  billingAddress: boolean
}

interface SearchResponse {
  addresses: IResponseItem[]
}

@Injectable({
  providedIn: 'root'
})
export class AddressConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string;

  private route = '';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
  }

  private static transformFromApi(response: IResponseItem): Address {
    return {
      ...response
    };
  }

  createDeliveryAddress(address: Partial<Address>, basketId: string, userId?: string): Observable<Address> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/addresses/delivery';
    return this.http.post<IResponseItem>(route, address).pipe(
      map(
        (response: IResponseItem) => AddressConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    );
  }

  createInvoiceAddress(address: Partial<Address>, basketId: string, userId?: string): Observable<Address> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/addresses/payment';
    return this.http.post<IResponseItem>(route, address).pipe(
      map(
        (response: IResponseItem) => AddressConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    );
  }

  updateAddress(addressId: string, address: Partial<Address>, userId?: string): Observable<Address> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/addresses/' + addressId;
    return this.http.patch<IResponseItem>(route, address).pipe(
      switchMap(
        (response: IResponseItem) => this.loadOne(addressId)
      ),
      catchError(error => throwError(error))
    );
  }

  loadOne(addressId: string): Observable<Address> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous/addresses/' + addressId;
    return this.http.get<IResponseItem>(route).pipe(
      map(
        (response: IResponseItem) => AddressConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    )
  }

  loadAll(): Observable<Address[]> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous/addresses';
    return this.http.get<SearchResponse>(route).pipe(
      map(
        (response: SearchResponse) => response.addresses.map(
          address => AddressConnectors.transformFromApi(address)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  protected getManyUrl() {
    return this._rootUrl + this.route;
  }

  protected getOneUrl(id: number) {
    return this._rootUrl + this.route + id;
  }
}
